/**
 * Notes for one record. Reused by every detail screen — notes are always
 * subject-scoped, so there is no standalone notes route.
 *
 * Writes go through the host route's action, keyed by `intent`. The action
 * completing revalidates that route's loader, which is what refreshes this list;
 * there is no cache to invalidate.
 */

import { useFetcher } from 'react-router-dom'
import type { Note, SubjectRef } from '@erp/contracts'
import { Button, EmptyState, Field, Textarea, TextInput } from '../ui'
import { formatDateTime } from '../format'

export function NotesPanel({ notes, subject }: { notes: readonly Note[]; subject: SubjectRef }) {
  const fetcher = useFetcher()
  const submitting = fetcher.state !== 'idle'

  // The list is loaded with deleted rows included so they can be restored; the
  // two groups are rendered apart rather than interleaved.
  const live = notes.filter((note) => note.deletedAt === null)
  const deleted = notes.filter((note) => note.deletedAt !== null)

  return (
    <div className="ax-panel">
      <fetcher.Form method="post" className="ax-note-form">
        <input type="hidden" name="intent" value="note.create" />
        <input type="hidden" name="subjectType" value={subject.type} />
        <input type="hidden" name="subjectId" value={subject.id} />
        <Field label="Add a note">
          <TextInput name="title" placeholder="Title (optional)" />
        </Field>
        <Textarea name="body" placeholder="What happened?" rows={3} />
        <Button variant="solid" type="submit" disabled={submitting}>
          {submitting ? 'Saving…' : 'Add note'}
        </Button>
      </fetcher.Form>

      {live.length === 0 ? (
        <EmptyState title="No notes yet" />
      ) : (
        <ul className="ax-note-list">
          {live.map((note) => (
            <li key={note.id} className="ax-note">
              {note.title ? <h4 className="ax-note-title">{note.title}</h4> : null}
              <p className="ax-note-body">{note.body}</p>
              <footer className="ax-note-meta">
                <time dateTime={note.createdAt}>{formatDateTime(note.createdAt)}</time>
                <fetcher.Form method="post">
                  <input type="hidden" name="intent" value="note.delete" />
                  <input type="hidden" name="noteId" value={note.id} />
                  <button className="ax-link-danger" type="submit">
                    Delete
                  </button>
                </fetcher.Form>
              </footer>
            </li>
          ))}
        </ul>
      )}

      {deleted.length > 0 ? (
        <details className="ax-note-deleted">
          <summary>{deleted.length} deleted</summary>
          <ul className="ax-note-list">
            {deleted.map((note) => (
              <li key={note.id} className="ax-note ax-note-is-deleted">
                {note.title ? <h4 className="ax-note-title">{note.title}</h4> : null}
                {/* The body is still here — that is the whole point of a soft delete. */}
                <p className="ax-note-body">{note.body}</p>
                <footer className="ax-note-meta">
                  <span>Deleted {note.deletedAt ? formatDateTime(note.deletedAt) : ''}</span>
                  <fetcher.Form method="post">
                    <input type="hidden" name="intent" value="note.restore" />
                    <input type="hidden" name="noteId" value={note.id} />
                    <button className="ax-link" type="submit">
                      Restore
                    </button>
                  </fetcher.Form>
                </footer>
              </li>
            ))}
          </ul>
        </details>
      ) : null}
    </div>
  )
}

